import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, ExternalLink, Pencil, X, FlaskConical, Inbox } from "lucide-react";

const EMPTY_DEMO = {
  client_name: "",
  slug: "",
  headline: "",
  subheadline: "",
  cta_text: "Get Started",
  primary_color: "#2E6BFF",
  accent_color: "#10B981",
  theme: "dark",
  about_title: "About Us",
  about_text: "",
  services: [
    { title: "", description: "" },
    { title: "", description: "" },
    { title: "", description: "" },
  ],
  published: true,
};

export default function AdminDemos() {
  const [demos, setDemos] = useState(null);
  const [editing, setEditing] = useState(null); // null | { ...form, id? }
  const [inquiriesFor, setInquiriesFor] = useState(null); // demo object
  const [inquiries, setInquiries] = useState([]);

  const load = async () => {
    try {
      const r = await api.get("/demos");
      setDemos(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  useEffect(() => { load(); }, []);

  const openInquiries = async (demo) => {
    setInquiriesFor(demo);
    try {
      const r = await api.get(`/demos/${demo.id}/inquiries`);
      setInquiries(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const remove = async (demo) => {
    if (!window.confirm(`Delete demo "${demo.client_name}" and its inquiries?`)) return;
    try {
      await api.delete(`/demos/${demo.id}`);
      toast.success("Demo deleted.");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  return (
    <div className="bg-[#0B0C10] min-h-[80vh]" data-testid="admin-demos">
      <section className="border-b border-[#1F2833] py-12">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-end justify-between flex-wrap gap-6">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-3">// DEMO.FACTORY</div>
            <h1 className="font-display text-3xl lg:text-5xl font-bold tracking-tighter">
              Demo showcase <span className="text-[#FF3355]">factory.</span>
            </h1>
            <p className="mt-3 text-sm text-[#C5C6C7]">
              Spin up fully branded prospect one-pagers at /demo/[clientname] — no code, no TraceElements branding.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin" className="inline-flex items-center gap-2 border border-[#1F2833] hover:border-[#B91C2C] text-[#C5C6C7] hover:text-[#F5F5F7] px-5 py-3 font-mono text-xs uppercase tracking-[0.22em]" data-testid="demos-back-admin">
              ← cockpit
            </Link>
            <button
              onClick={() => setEditing({ ...EMPTY_DEMO, services: EMPTY_DEMO.services.map((s) => ({ ...s })) })}
              data-testid="demos-new"
              className="inline-flex items-center gap-2 bg-[#B91C2C] text-[#0B0C10] px-5 py-3 font-mono text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#FF3355]"
            >
              <Plus size={14} /> new demo
            </button>
          </div>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          {demos === null && <div className="font-mono text-sm text-[#C5C6C7]">&gt; loading<span className="te-caret">_</span></div>}
          {demos !== null && demos.length === 0 && (
            <div className="border border-[#1F2833] p-10 text-center font-mono text-sm text-[#C5C6C7]" data-testid="demos-empty">
              No demos yet — create your first prospect site.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {(demos || []).map((d) => (
              <div key={d.id} className="border border-[#1F2833] bg-[#0B0C10] p-6" data-testid={`demo-card-${d.slug}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="h-4 w-4 rounded-full border border-white/20" style={{ backgroundColor: d.primary_color }} />
                    <span className="h-4 w-4 rounded-full border border-white/20" style={{ backgroundColor: d.accent_color }} />
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#C5C6C7]">{d.theme}</span>
                  </div>
                  <span className={`font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 border ${d.published ? "border-[#34d399]/50 text-[#34d399]" : "border-[#C5C6C7]/30 text-[#C5C6C7]"}`} data-testid={`demo-status-${d.slug}`}>
                    {d.published ? "● live" : "○ draft"}
                  </span>
                </div>
                <h3 className="mt-4 font-display text-xl font-bold tracking-tight">{d.client_name}</h3>
                <a href={`/demo/${d.slug}`} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1.5 font-mono text-xs text-[#FF3355] hover:underline" data-testid={`demo-open-${d.slug}`}>
                  /demo/{d.slug} <ExternalLink size={11} />
                </a>
                <p className="mt-3 text-xs text-[#C5C6C7] line-clamp-2 min-h-[2rem]">{d.headline || "—"}</p>
                <div className="mt-5 flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => setEditing({ ...d, services: normalizeServices(d.services) })}
                    data-testid={`demo-edit-${d.slug}`}
                    className="inline-flex items-center gap-1.5 border border-[#1F2833] hover:border-[#FF3355] text-[#C5C6C7] hover:text-[#F5F5F7] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em]"
                  >
                    <Pencil size={11} /> edit
                  </button>
                  <button
                    onClick={() => openInquiries(d)}
                    data-testid={`demo-inquiries-${d.slug}`}
                    className="inline-flex items-center gap-1.5 border border-[#1F2833] hover:border-[#FF3355] text-[#C5C6C7] hover:text-[#F5F5F7] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em]"
                  >
                    <Inbox size={11} /> inquiries ({d.inquiry_count ?? 0})
                  </button>
                  <button
                    onClick={() => remove(d)}
                    data-testid={`demo-delete-${d.slug}`}
                    className="inline-flex items-center gap-1.5 border border-[#1F2833] hover:border-[#B91C2C] text-[#C5C6C7] hover:text-[#FF3355] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em]"
                  >
                    <Trash2 size={11} /> delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {editing && (
        <DemoEditor
          demo={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); toast.success("Demo saved."); }}
        />
      )}

      {inquiriesFor && (
        <div className="fixed inset-0 z-50 bg-[#0B0C10]/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={() => setInquiriesFor(null)}>
          <div className="te-terminal-window w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()} data-testid="demo-inquiries-modal">
            <div className="border-b border-[#1F2833] px-5 py-3 flex items-center justify-between">
              <div className="font-mono text-xs uppercase tracking-[0.22em] text-[#FF3355]">// INQUIRIES · {inquiriesFor.client_name}</div>
              <button onClick={() => setInquiriesFor(null)} className="text-[#C5C6C7] hover:text-[#F5F5F7]" data-testid="demo-inquiries-close"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              {inquiries.length === 0 && <div className="font-mono text-sm text-[#C5C6C7]" data-testid="demo-inquiries-empty">No inquiries yet.</div>}
              {inquiries.map((q) => (
                <div key={q.id} className="border border-[#1F2833] p-4" data-testid={`demo-inquiry-${q.id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <span className="font-bold text-sm">{q.name} <span className="text-[#C5C6C7] font-normal">· {q.email}</span></span>
                    <span className="font-mono text-[10px] text-[#C5C6C7]">{new Date(q.created_at).toLocaleString()}</span>
                  </div>
                  <p className="mt-2 text-sm text-[#C5C6C7] leading-relaxed">{q.message}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function normalizeServices(services) {
  const out = [...(services || [])].slice(0, 3).map((s) => ({ title: s.title || "", description: s.description || "" }));
  while (out.length < 3) out.push({ title: "", description: "" });
  return out;
}

/* ============ DEMO EDITOR MODAL ============ */
function DemoEditor({ demo, onClose, onSaved }) {
  const [form, setForm] = useState(demo);
  const [busy, setBusy] = useState(false);
  const isNew = !demo.id;

  const setService = (i, key, value) => {
    const services = form.services.map((s, idx) => (idx === i ? { ...s, [key]: value } : s));
    setForm({ ...form, services });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      if (isNew) await api.post("/demos", form);
      else await api.patch(`/demos/${demo.id}`, form);
      await onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0B0C10]/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="te-terminal-window w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()} data-testid="demo-editor-modal">
        <div className="border-b border-[#1F2833] px-5 py-3 flex items-center justify-between">
          <div className="font-mono text-xs uppercase tracking-[0.22em] text-[#FF3355]">{isNew ? "// NEW.DEMO" : `// EDIT.DEMO · ${demo.slug}`}</div>
          <button onClick={onClose} className="text-[#C5C6C7] hover:text-[#F5F5F7]" data-testid="demo-editor-close"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-6 lg:p-8 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <DemoField label="client name" value={form.client_name} onChange={(v) => setForm({ ...form, client_name: v })} testid="demo-form-name" required />
            <DemoField label="url slug (optional — auto from name)" value={form.slug || ""} onChange={(v) => setForm({ ...form, slug: v })} testid="demo-form-slug" />
          </div>
          <DemoField label="hero headline" value={form.headline} onChange={(v) => setForm({ ...form, headline: v })} testid="demo-form-headline" required />
          <DemoField label="hero subheadline" value={form.subheadline} onChange={(v) => setForm({ ...form, subheadline: v })} testid="demo-form-subheadline" textarea />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <DemoField label="cta button text" value={form.cta_text} onChange={(v) => setForm({ ...form, cta_text: v })} testid="demo-form-cta" />
            <div>
              <DemoLabel text="primary color" />
              <div className="flex items-center gap-3">
                <input type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} data-testid="demo-form-primary-color" className="h-10 w-14 bg-transparent border border-[#1F2833] cursor-pointer" />
                <span className="font-mono text-xs text-[#C5C6C7]">{form.primary_color}</span>
              </div>
            </div>
            <div>
              <DemoLabel text="accent color" />
              <div className="flex items-center gap-3">
                <input type="color" value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} data-testid="demo-form-accent-color" className="h-10 w-14 bg-transparent border border-[#1F2833] cursor-pointer" />
                <span className="font-mono text-xs text-[#C5C6C7]">{form.accent_color}</span>
              </div>
            </div>
          </div>
          <div>
            <DemoLabel text="theme" />
            <div className="inline-flex border border-[#1F2833]">
              {["dark", "light"].map((t) => (
                <button
                  type="button" key={t}
                  onClick={() => setForm({ ...form, theme: t })}
                  data-testid={`demo-form-theme-${t}`}
                  className={`px-5 py-2.5 font-mono text-xs uppercase tracking-[0.22em] ${form.theme === t ? "bg-[#B91C2C] text-[#0B0C10]" : "text-[#C5C6C7] hover:text-[#F5F5F7]"}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          <DemoField label="about section title" value={form.about_title} onChange={(v) => setForm({ ...form, about_title: v })} testid="demo-form-about-title" />
          <DemoField label="about section text" value={form.about_text} onChange={(v) => setForm({ ...form, about_text: v })} testid="demo-form-about-text" textarea />

          <div>
            <DemoLabel text="services — 3 cards" />
            <div className="space-y-4">
              {form.services.map((s, i) => (
                <div key={i} className="border border-[#1F2833] p-4 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid={`demo-form-service-${i}`}>
                  <DemoField label={`card ${i + 1} title`} value={s.title} onChange={(v) => setService(i, "title", v)} testid={`demo-form-service-${i}-title`} />
                  <DemoField label={`card ${i + 1} description`} value={s.description} onChange={(v) => setService(i, "description", v)} testid={`demo-form-service-${i}-desc`} />
                </div>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-3 font-mono text-xs text-[#C5C6C7]">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="demo-form-published" className="accent-[#FF3355]" />
            published (visible at /demo/{form.slug || "slug"})
          </label>

          <button type="submit" disabled={busy} data-testid="demo-form-submit" className="w-full bg-[#B91C2C] text-[#0B0C10] px-5 py-3 font-mono text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#FF3355] disabled:opacity-50">
            {busy ? "SAVING..." : isNew ? "[ CREATE DEMO SITE ]" : "[ SAVE CHANGES ]"}
          </button>
        </form>
      </div>
    </div>
  );
}

function DemoLabel({ text }) {
  return <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7] mb-2">{text}</div>;
}

function DemoField({ label, value, onChange, testid, textarea, required }) {
  return (
    <div>
      <DemoLabel text={label + (required ? " *" : "")} />
      {textarea ? (
        <textarea
          value={value} onChange={(e) => onChange(e.target.value)} required={required} rows={3}
          data-testid={testid}
          className="w-full bg-slate-950 border border-[#1F2833] text-[#F5F5F7] px-3 py-2 font-mono text-sm focus:border-[#FF3355] outline-none resize-y"
        />
      ) : (
        <input
          value={value} onChange={(e) => onChange(e.target.value)} required={required}
          data-testid={testid}
          className="w-full bg-slate-950 border border-[#1F2833] text-[#F5F5F7] px-3 py-2 font-mono text-sm focus:border-[#FF3355] outline-none"
        />
      )}
    </div>
  );
}
